import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@/common/components/Drawer/Drawer';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useGetOperationDetail } from '@/common/integrations/menhir/employee/operations/operations.hooks';
import {
  isChildcareOperation,
  isReimbursementExpenseOperation,
  isVoucherExpenseOperation,
} from '@/common/utils/operations';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  ChildcareDetailDrawer,
  ChildcareDetailDrawerTitle,
} from './components/ChildcareDetailDrawer/ChildcareDetailDrawer';
import { FailedDetailDrawer } from './components/FailedDetailDrawer/FailedDetailDrawer';
import { MealDetailDrawer } from './components/MealDetailDrawer/MealDetailDrawer';
import {
  ReimbursementDetailDrawer,
  ReimbursementDetailDrawerTitle,
} from './components/ReimbursementDetailDrawer/ReimbursementDetailDrawer';

export const OperationDetail = () => {
  const { setParams } = useRouter();
  const { t } = useTranslation();
  const queryParams = useLocalSearchParams<{ operationId: string }>();
  const { operation, isInitialLoading } = useGetOperationDetail(
    queryParams?.operationId ?? '',
    {
      enabled: !!queryParams.operationId,
    }
  );

  const onClose = () => {
    setParams({ operationId: '' });
  };

  useEffect(() => {
    if (operation) {
      AnalyticsService.trackPageView('operation_detail_page', {
        operation_status: operation.status,
        operation_rejected_reason:
          (operation.descriptionParams?.find(
            (param) => param.key === 'rejection_reason'
          )?.value as string) ?? ('generic_error' as string),
      });
    }
  }, [operation]);

  if (isInitialLoading) {
    return (
      <Drawer onClose={onClose}>
        <LoadingPlaceholder />
      </Drawer>
    );
  }

  if (!operation) {
    return null;
  }

  if (isReimbursementExpenseOperation(operation)) {
    return (
      <Drawer
        onClose={onClose}
        title={<ReimbursementDetailDrawerTitle operation={operation} />}
      >
        <ReimbursementDetailDrawer operation={operation} />
      </Drawer>
    );
  } else if (
    isVoucherExpenseOperation(operation) &&
    operation.status !== 'rejected'
  ) {
    return (
      <Drawer onClose={onClose} title={t('general.labels.transaction')}>
        <MealDetailDrawer operation={operation} />
      </Drawer>
    );
  } else if (operation.status === 'rejected') {
    return (
      <Drawer onClose={onClose} title={t('general.labels.transaction')}>
        <FailedDetailDrawer operation={operation} />
      </Drawer>
    );
  } else if (isChildcareOperation(operation)) {
    return (
      <Drawer
        onClose={onClose}
        title={<ChildcareDetailDrawerTitle operation={operation} />}
      >
        <ChildcareDetailDrawer operation={operation} />
      </Drawer>
    );
  }

  return null;
};
